import { type MenuInputOption } from '@saviynt/design-system';

import type {
  DataProvider,
  FieldElement,
  FormValues,
  PreviewDataProviderPayload,
} from '../../../../shared/types';

import { getDataProviderPreviewPageSize } from '../../../../shared/utils/dataProviderPageSize';
import { DATA_PROVIDER } from '../../../FormBuilder';
import { applyValueMatching } from '../valueProcessing';

import {
  areDependenciesSatisfied,
  buildDependencies,
  type InitialDataForDependencies,
} from './dependencies';
import {
  isValidMenuInputOption,
  validateOptions,
  validateSelectedOptions,
} from './validation';

/**
 * Escapes a string for safe use in a CSS attribute selector [attr="value"].
 * Handles " and \ which would otherwise break the selector.
 * Uses manual escape to avoid reliance on CSS.escape (which may not be in bundle).
 */
export const escapeForCssAttributeSelector = (value: string): string =>
  String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

// ============================================================================
// Value Matching Utilities
// ============================================================================

/**
 * Creates a key for tracking options and processed value changes
 */
export const createOptionsValueKey = (
  options: MenuInputOption[],
  processedValue: MenuInputOption | MenuInputOption[] | null
): string => {
  const optionsKey = JSON.stringify(
    options.map((opt) => ({ value: opt.value, label: opt.label }))
  );
  let processedValueKey = '';

  if (processedValue) {
    if (Array.isArray(processedValue)) {
      processedValueKey = JSON.stringify(
        processedValue.map((v: MenuInputOption) => String(v.value))
      );
    } else if (
      typeof processedValue === 'object' &&
      'value' in processedValue
    ) {
      processedValueKey = JSON.stringify(String(processedValue.value));
    }
  }

  return `${optionsKey}|${processedValueKey}`;
};

/**
 * For paginated HTTP/SQL multiselect, the current `options` slice may omit values
 * that are still selected on the form. Merge checked rows from this page with
 * selections from `processedValue` whose values are not on this page.
 */
export const mergePaginatedMultiSelectSelections = (
  pageOptions: MenuInputOption[],
  checkedOnPage: MenuInputOption[],
  processedValue: MenuInputOption | MenuInputOption[] | null | undefined
): MenuInputOption[] => {
  const pageValueSet = new Set(pageOptions.map((o) => String(o.value)));
  const fromPage = validateSelectedOptions(checkedOnPage);

  if (!processedValue) {
    return fromPage;
  }

  const valueArray = Array.isArray(processedValue)
    ? processedValue
    : [processedValue];
  const preserved = validateSelectedOptions(
    valueArray.filter(
      (o): o is MenuInputOption =>
        isValidMenuInputOption(o) && !pageValueSet.has(String(o.value))
    )
  );

  const merged = new Map<string, MenuInputOption>();

  preserved.forEach((p) => {
    merged.set(String(p.value), { ...p, isChecked: true });
  });

  fromPage.forEach((p) => {
    merged.set(String(p.value), p);
  });

  return Array.from(merged.values());
};

/**
 * Applies value matching and updates selected options for MultiSelect
 */
export const applyValueMatchingAndUpdateSelected = (
  options: MenuInputOption[],
  dataProvider: DataProvider,
  processedValue: MenuInputOption | MenuInputOption[] | null,
  setOptions: (options: MenuInputOption[]) => void,
  setSelectedOptions: (options: MenuInputOption[]) => void,
  paginationEnabled = false
): MenuInputOption[] => {
  const matchedOptions = applyValueMatchingToOptions(
    options,
    dataProvider,
    processedValue
  );
  const validatedOptions = validateOptions(matchedOptions);

  setOptions(validatedOptions);

  const checkedOnPage = validatedOptions.filter(
    (option: MenuInputOption) => option.isChecked
  );

  if (
    paginationEnabled &&
    processedValue &&
    (dataProvider.type === DATA_PROVIDER.HTTP ||
      dataProvider.type === DATA_PROVIDER.SQL)
  ) {
    setSelectedOptions(
      mergePaginatedMultiSelectSelections(
        validatedOptions,
        checkedOnPage,
        processedValue
      )
    );
  } else {
    setSelectedOptions(validateSelectedOptions(checkedOnPage));
  }

  return validatedOptions;
};

// Get placeholder text based on dependencies
export const getFieldPlaceholder = (
  dataProvider: DataProvider,
  formValues: FormValues,
  defaultPlaceholder?: string,
  initialData?: InitialDataForDependencies
): string => {
  const dependenciesNotSatisfied = !areDependenciesSatisfied(
    dataProvider,
    formValues,
    initialData
  );

  if (dependenciesNotSatisfied) {
    return 'Please select required fields first';
  }

  return defaultPlaceholder ?? 'Select';
};

/**
 * Checks if an element is actually visible in the viewport
 * (not just in DOM, but rendered and visible to user)
 */
const isElementVisible = (element: HTMLElement): boolean => {
  // Check if element has dimensions (not display: none)
  if (element.offsetWidth === 0 || element.offsetHeight === 0) {
    return false;
  }

  // Check computed style for visibility
  const style = window.getComputedStyle(element);

  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  );
};

/**
 * Finds visible dropdown elements by looking for common dropdown menu patterns.
 * Only returns elements that are actually visible to the user (not just in DOM).
 *
 * @param dataTestId - The data-testid attribute to help locate the dropdown
 * @returns The dropdown menu element if found and visible, null otherwise
 */
export const findDropdownElement = (dataTestId: string): HTMLElement | null => {
  const componentWrapper = document.querySelector(
    `[data-testid="${escapeForCssAttributeSelector(dataTestId)}"]`
  );

  if (componentWrapper) {
    // Look for dropdown menu inside the component
    // MenuInput typically renders dropdown with role="listbox" or similar patterns
    const dropdownByRole = componentWrapper.querySelector<HTMLElement>(
      '[role="listbox"], [role="menu"]'
    );

    if (dropdownByRole && isElementVisible(dropdownByRole)) {
      return dropdownByRole;
    }

    // Look for common dropdown class patterns (works with CSS modules partial matches)
    const dropdownByClass = componentWrapper.querySelector<HTMLElement>(
      '[class*="dropdown"], [class*="Dropdown"], [class*="menu"], [class*="Menu"], [class*="listbox"]'
    );

    if (dropdownByClass && isElementVisible(dropdownByClass)) {
      return dropdownByClass;
    }
  }

  // Fallback: search globally for recently opened dropdowns
  // This handles portaled dropdowns that render outside the component tree
  const portaledDropdowns = Array.from(
    document.querySelectorAll<HTMLElement>('[role="listbox"], [role="menu"]')
  );

  const visibleDropdown = portaledDropdowns.find((dropdown) =>
    isElementVisible(dropdown)
  );

  return visibleDropdown ?? null;
};

// Common payload builder
export const buildApiPayload = (
  dataProvider: DataProvider,
  fieldId: string,
  canvasFields: FieldElement[],
  dataProviders: Record<string, DataProvider>,
  formValues: FormValues,
  currentPage: number,
  initialData?: InitialDataForDependencies
): PreviewDataProviderPayload => {
  const dependencies = buildDependencies(dataProvider, formValues, initialData);

  return {
    form: {
      ui: canvasFields,
      dataProviders,
      version: '1.0',
    },
    formItemId: fieldId,
    page: currentPage,
    pageSize: getDataProviderPreviewPageSize(dataProvider),
    dependencies,
    searchText: '',
  };
};

// Check if pagination is enabled for HTTP and SQL providers
export const isPaginationEnabled = (dataProvider: DataProvider): boolean => {
  if (!dataProvider?.type) {
    return false;
  }

  if (dataProvider.type === DATA_PROVIDER.HTTP) {
    return dataProvider.httpApi.pagination?.pageSize !== undefined;
  }

  if (dataProvider.type === DATA_PROVIDER.SQL) {
    return 'mapping' in dataProvider.sql;
  }

  if (dataProvider.type === DATA_PROVIDER.DATASTORE) {
    return false;
  }

  return false;
};

// Get fixed options and apply value matching
export const getFixedOptions = (
  dataProvider: DataProvider,
  processedValue: MenuInputOption | MenuInputOption[] | null
): MenuInputOption[] => {
  if (!dataProvider || dataProvider.type !== DATA_PROVIDER.FIXED) {
    return [];
  }

  if (!('fixed' in dataProvider)) {
    return [];
  }

  const menuOptions: MenuInputOption[] = dataProvider.fixed.map((option) => ({
    value: option.value,
    label: option.label,
    isChecked: false,
    ...(option.secondaryLabel && { secondaryLabel: option.secondaryLabel }),
  }));

  return applyValueMatching(menuOptions, processedValue);
};

// Apply value matching for HTTP and SQL providers when options are loaded
export const applyValueMatchingToOptions = (
  options: MenuInputOption[],
  dataProvider: DataProvider,
  processedValue: MenuInputOption | MenuInputOption[] | null
): MenuInputOption[] => {
  if (
    options.length > 0 &&
    dataProvider &&
    (dataProvider.type === DATA_PROVIDER.HTTP ||
      dataProvider.type === DATA_PROVIDER.SQL ||
      dataProvider.type === DATA_PROVIDER.DATASTORE) &&
    processedValue
  ) {
    return applyValueMatching(options, processedValue);
  }

  return options;
};
